import type { Role } from '@aisolution/shared';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  must_change?: boolean;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...init,
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  return data as T;
}

export const api = {
  setupStatus: () => req<{ needsSetup: boolean }>('/auth/setup/status'),
  setup: (b: { email: string; name: string; password: string }) =>
    req<SessionUser>('/auth/setup', { method: 'POST', body: JSON.stringify(b) }),
  login: (b: { email: string; password: string }) =>
    req<SessionUser>('/auth/login', { method: 'POST', body: JSON.stringify(b) }),
  registrationStatus: () => req<{ open: boolean; domain: string }>('/auth/registration-status'),
  register: (b: { email: string; name: string; password: string; role: string }) =>
    req<{ pending: true; message: string }>('/auth/register', { method: 'POST', body: JSON.stringify(b) }),
  getPendingUsers: () => req<{ pending: PendingUser[] }>('/admin/pending-users'),
  approvePendingUser: (uid: string) => req<{ ok: true }>(`/admin/pending-users/${uid}/approve`, { method: 'POST' }),
  rejectPendingUser: (uid: string) => req<{ ok: true }>(`/admin/pending-users/${uid}`, { method: 'DELETE' }),
  listUsers: () => req<{ users: UserRow[] }>('/admin/users'),
  updateUser: (uid: string, b: { role?: string; active?: boolean }) => req<{ ok: true }>(`/admin/users/${uid}`, { method: 'PUT', body: JSON.stringify(b) }),
  getRegistration: () => req<{ open: boolean; domain: string }>('/admin/registration'),
  setRegistration: (open: boolean) => req<{ ok: true; open: boolean }>('/admin/registration', { method: 'PUT', body: JSON.stringify({ open }) }),
  logout: () => req<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => req<SessionUser | null>('/auth/me'),
  changePassword: (b: { current_password?: string; new_password: string }) =>
    req<{ ok: true }>('/auth/change-password', { method: 'POST', body: JSON.stringify(b) }),
  resetUserPassword: (uid: string) => req<{ ok: true; temp_password: string }>(`/admin/users/${uid}/reset-password`, { method: 'POST' }),
  listProjects: () => req<{ projects: ProjectRow[] }>('/projects'),
  createProject: (b: Record<string, string>) =>
    req<ProjectRow>('/projects', { method: 'POST', body: JSON.stringify(b) }),
  getProject: (id: string) => req<ProjectRow & { fa_input?: string | null }>(`/projects/${id}`),
  saveFaDesign: (b: { id?: string; name: string; cityAhj?: string; input: unknown; sell_price: number; categories?: unknown }) =>
    req<{ ok: true; id: string }>(`/projects/fa-save`, { method: 'POST', body: JSON.stringify(b) }),
  deleteProject: (id: string) => req<{ ok: true }>(`/projects/${id}`, { method: 'DELETE' }),
  setOutcome: (id: string, b: { outcome: string | null; amount?: number | null; competitor?: string | null; reason?: string | null }) =>
    req<{ ok: true; outcome: string | null }>(`/projects/${id}/outcome`, { method: 'PUT', body: JSON.stringify(b) }),
  setBidScore: (id: string, b: { factors: Record<string, number>; notes?: string }) =>
    req<{ ok: true; bid_score: string }>(`/projects/${id}/bid-score`, { method: 'PUT', body: JSON.stringify(b) }),
  getAnalytics: () => req<Analytics>(`/analytics`),
  ingest: (id: string, b: { filename: string; pages: { page: number; text: string }[]; images?: { page: number; dataUrl: string }[]; files?: { mimeType: string; fileUri: string }[] }) =>
    req<{ ok: true; source: 'gemini' | 'claude' | 'sample' | 'unavailable'; warning?: string; counts: Record<string, number> }>(
      `/projects/${id}/ingest`, { method: 'POST', body: JSON.stringify(b) }),
  getAnalysis: (id: string) => req<AnalysisPayload>(`/projects/${id}/analysis`),
  uploadDocument: async (id: string, file: File, type = 'plans') => {
    const res = await fetch(`/api/projects/${id}/documents?filename=${encodeURIComponent(file.name)}&type=${type}`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'content-type': file.type || 'application/octet-stream' }, body: file,
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; id?: string; error?: string };
    if (!res.ok) throw new Error(data.error ?? `Upload failed (${res.status})`);
    return data;
  },
  documentUrl: (id: string, docId: string) => `/api/projects/${id}/documents/${docId}/file`,
  toggleSystem: (id: string, systemId: string, selected: boolean) =>
    req<{ ok: true; selected: boolean }>(`/projects/${id}/systems/${systemId}`,
      { method: 'PUT', body: JSON.stringify({ selected }) }),
  getMappings: (id: string) => req<MappingPayload>(`/projects/${id}/mappings`),
  proposeMappings: (id: string) =>
    req<{ ok: true; total: number; needs_review: number; gate: MappingGate }>(
      `/projects/${id}/mappings/propose`, { method: 'POST' }),
  updateMapping: (id: string, symbolId: string, b: { device_key?: string | null; status?: MappingStatus; ofci?: boolean }) =>
    req<{ ok: true; gate: MappingGate }>(`/projects/${id}/mappings/${symbolId}`,
      { method: 'PUT', body: JSON.stringify(b) }),
  confirmAllMappings: (id: string) =>
    req<{ ok: true; confirmed: number; remaining_unmapped: number; gate: MappingGate }>(
      `/projects/${id}/mappings/confirm-all`, { method: 'POST' }),
  editField: (id: string, b: { field: string; value?: string; status?: 'confirmed' | 'edited' }) =>
    req<{ ok: true }>(`/projects/${id}/extraction`, { method: 'PUT', body: JSON.stringify(b) }),
  getCountSheet: (id: string) => req<CountSheetPayload>(`/projects/${id}/count-sheet`),
  importCounts: (id: string, rows: { symbol_id: string; qty: string; comments: string }[]) =>
    req<{ ok: true; report: ValidationReport }>(`/projects/${id}/counts`,
      { method: 'POST', body: JSON.stringify({ rows }) }),
  generateBom: (id: string) => req<{ ok: true; result: BomResult }>(`/projects/${id}/bom/generate`, { method: 'POST' }),
  getBom: (id: string) => req<BomPayload>(`/projects/${id}/bom`),
  getCosting: (id: string) => req<Costing>(`/projects/${id}/costing`),
  getUnitPrices: (id: string) => req<{ unit_prices: UnitPrice[] }>(`/projects/${id}/unit-prices`),
  repriceBom: (id: string) => req<{ ok: true; summary: RepriceSummary }>(`/projects/${id}/bom/reprice`, { method: 'POST' }),
  uploadPrices: (id: string, rows: Record<string, unknown>[]) =>
    req<{ ok: true; inserted: number; skipped: number }>(`/projects/${id}/prices`, { method: 'POST', body: JSON.stringify({ rows }) }),
  saveWageSettings: (id: string, b: { wage_type?: string; state?: string; county?: string; shift?: string }) =>
    req<{ ok: true }>(`/projects/${id}/wage-settings`, { method: 'PUT', body: JSON.stringify(b) }),
  getTravel: (id: string) => req<{ travel: TravelRow[] }>(`/projects/${id}/travel`),
  addTravel: (id: string, b: Record<string, unknown>) => req<{ ok: true; id: string }>(`/projects/${id}/travel`, { method: 'POST', body: JSON.stringify(b) }),
  deleteTravel: (id: string, tid: string) => req<{ ok: true }>(`/projects/${id}/travel/${tid}`, { method: 'DELETE' }),
  getMarkups: (id: string) => req<{ markups: MarkupRow[]; defaults: { name: string; label: string; pct: number; basis: string }[] }>(`/projects/${id}/markups`),
  saveMarkups: (id: string, markups: { name: string; pct: number }[]) =>
    req<{ ok: true; floored?: string[] }>(`/projects/${id}/markups`, { method: 'PUT', body: JSON.stringify({ markups }) }),
  generateRfis: (id: string) => req<{ ok: true; count: number }>(`/projects/${id}/rfis/generate`, { method: 'POST' }),
  getRfis: (id: string) => req<{ rfis: RfiRow[] }>(`/projects/${id}/rfis`),
  generateChangeOrders: (id: string) => req<{ ok: true; count: number }>(`/projects/${id}/change-orders/generate`, { method: 'POST' }),
  getChangeOrders: (id: string) => req<{ change_orders: CoRow[] }>(`/projects/${id}/change-orders`),
  saveEstimate: (id: string) => req<{ ok: true; version: number; final_sell_price: number }>(`/projects/${id}/estimate`, { method: 'POST' }),
  getEstimate: (id: string) => req<{ estimate: EstimateRow | null }>(`/projects/${id}/estimate`),
  pdfUrl: (id: string, kind: 'supplier-pdf' | 'estimate-pdf' | 'proposal-short-pdf' | 'proposal-full-pdf' | 'proposal-word') => `/api/projects/${id}/${kind}`,
  getQcReview: (id: string) => req<QcReview>(`/projects/${id}/qc-review`),
  getScopeCompleteness: (id: string) => req<ScopeCompleteness>(`/projects/${id}/scope-completeness`),
  getCloseout: (id: string) => req<CloseoutData>(`/projects/${id}/closeout`),
  saveCloseout: (id: string, b: Record<string, unknown>) => req<{ ok: true }>(`/projects/${id}/closeout`, { method: 'PUT', body: JSON.stringify(b) }),
  getWageOverrides: (id: string) => req<{ rates: WageOverrideRow[]; wage_type: string }>(`/projects/${id}/wage-overrides`),
  setWageOverride: (id: string, b: { category: string; hourly: number | null }) => req<{ ok: true; floored?: boolean; applied?: number }>(`/projects/${id}/wage-overrides`, { method: 'PUT', body: JSON.stringify(b) }),
  // Admin
  updatePrevailingWages: () => req<WageUpdateSummary>(`/admin/update-prevailing-wages`, { method: 'POST' }),
  getWageUpdateLog: () => req<{ log: WageUpdateRow[] }>(`/admin/wage-update-log`),
  getDefaultMarkups: () => req<{ markups: { name: string; label: string; pct: number; basis: string }[] }>(`/admin/default-markups`),
  saveDefaultMarkups: (markups: { name: string; pct: number }[]) => req<{ ok: true }>(`/admin/default-markups`, { method: 'PUT', body: JSON.stringify({ markups }) }),
  getCompanyProfile: () => req<{ sections: CompanySection[] }>(`/admin/company-profile`),
  updateCompanyProfile: (section: string, b: { title?: string; body?: string }) =>
    req<{ ok: true }>(`/admin/company-profile/${section}`, { method: 'PUT', body: JSON.stringify(b) }),
  getPersonnel: () => req<{ personnel: Personnel[] }>(`/admin/personnel`),
  addPersonnel: (b: { name: string; role: string }) => req<{ ok: true; id: string }>(`/admin/personnel`, { method: 'POST', body: JSON.stringify(b) }),
  deletePersonnel: (pid: string) => req<{ ok: true }>(`/admin/personnel/${pid}`, { method: 'DELETE' }),
  getWageRates: (q?: { wage_type?: string; state?: string }) => {
    const p = new URLSearchParams();
    if (q?.wage_type) p.set('wage_type', q.wage_type);
    if (q?.state) p.set('state', q.state);
    const qs = p.toString();
    return req<{ rates: WageRate[] }>(`/admin/wage-rates${qs ? `?${qs}` : ''}`);
  },
  getLaborCalibration: () => req<{ calibration: LaborCalibration[] }>(`/admin/labor-calibration`),
  updateLaborCalibration: (cat: string, b: { add_est?: number; add_actual?: number; reset?: boolean; note?: string }) =>
    req<{ ok: true; factor: number }>(`/admin/labor-calibration/${cat}`, { method: 'PUT', body: JSON.stringify(b) }),
  addWageRate: (b: Partial<WageRate>) => req<{ ok: true; id: string }>(`/admin/wage-rates`, { method: 'POST', body: JSON.stringify(b) }),
  updateWageRate: (rid: string, b: Partial<WageRate>) => req<{ ok: true }>(`/admin/wage-rates/${rid}`, { method: 'PUT', body: JSON.stringify(b) }),
  deleteWageRate: (rid: string) => req<{ ok: true }>(`/admin/wage-rates/${rid}`, { method: 'DELETE' }),
  getCodes: (q?: { discipline?: string; q?: string }) => {
    const p = new URLSearchParams();
    if (q?.discipline) p.set('discipline', q.discipline);
    if (q?.q) p.set('q', q.q);
    const qs = p.toString();
    return req<{ codes: CodeLibRow[] }>(`/admin/codes${qs ? `?${qs}` : ''}`);
  },
  addCode: (b: { level: string; code: string; title?: string; applies_to_system?: string; discipline?: string; section?: string; edition?: string; url?: string; applies_in?: string; notes?: string }) =>
    req<{ ok: true; id: string }>(`/admin/codes`, { method: 'POST', body: JSON.stringify(b) }),
  importRatesCsv: async (csv: string) => {
    const res = await fetch('/api/admin/import-csv', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'text/csv' }, body: csv });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error ?? `${res.status}`);
    return data as { ok: true; report: ImportReport };
  },
  getRates: (q?: { limit?: number; offset?: number; q?: string }) => {
    const p = new URLSearchParams();
    if (q?.limit) p.set('limit', String(q.limit));
    if (q?.offset) p.set('offset', String(q.offset));
    if (q?.q) p.set('q', q.q);
    const qs = p.toString();
    return req<{ rates: RateRow[]; total: number; limit: number; offset: number }>(`/admin/rates${qs ? `?${qs}` : ''}`);
  },
  wipeRates: () => req<{ ok: true; wiped: number }>(`/admin/rates`, { method: 'DELETE' }),
};

export interface ImportReport {
  total_csv_rows: number; inserted: number; skipped_no_part: number; skipped_no_price: number;
  skipped_labor_or_lump: number; skipped_placeholder: number; skipped_duplicate: number;
  by_source: Record<string, number>;
}
export interface RateRow {
  id: string; part_number: string | null; manufacturer: string | null; description: string | null;
  item_name: string | null; unit: string | null; unit_price: number;
  supplier: string | null; quote_number: string | null; quote_date: string | null;
  source: string; csi_section: string | null; discipline: string | null; stale: number; notes: string | null;
}

export interface QcCheck { key: string; label: string; status: 'pass' | 'warn' | 'fail'; detail: string }
export interface QcReview { checks: QcCheck[]; summary: { pass: number; warn: number; fail: number } }
export interface ScopeItem { id: string; label: string; group: string; status: 'covered' | 'verify' | 'missing'; note: string }
export interface ScopeCompleteness { items: ScopeItem[]; summary: { covered: number; verify: number; missing: number } }
export interface CloseoutRow { project_id: string; actual_material: number | null; actual_labor_cost: number | null; actual_labor_hours: number | null; actual_other: number | null; notes: string | null; completed_at: string | null }
export interface CloseoutData { estimated: { material: number; labor: number; labor_hours: number; total_cost: number; sell: number } | null; closeout: CloseoutRow | null }
export interface WageUpdateSummary { run_at: string; period: string; period_changed: boolean; counties: number; applied: number; held: number; unchanged: number; errors: number; items: Record<string, unknown>[] }
export interface WageUpdateRow { run_at: string; county: string; classification: string; old_base: number | null; new_base: number | null; old_fringe: number | null; new_fringe: number | null; effective_date: string | null; expiration_date: string | null; status: string; reason: string | null }
export interface WageOverrideRow { category: string; classification: string; default_hourly: number; override_hourly: number | null; is_sample: boolean }
export interface GroupStat { name: string; won: number; lost: number; decided: number; winRate: number }
export interface LossSample { name: string; competitor: string | null; ourBid: number; winning: number; pctOver: number }
export interface Analytics {
  overall: { bids: number; won: number; lost: number; noBid: number; open: number; winRate: number | null; wonValue: number };
  byEstimator: GroupStat[];
  byCustomer: GroupStat[];
  lossGap: { count: number; avgPctOver: number | null; samples: LossSample[] };
}
export interface CompanySection { section: string; title: string; body: string; sort: number; is_placeholder: number }
export interface Personnel { id: string; name: string; role: string; active: number }
export interface CodeLibRow {
  id: string; level: string; code: string; title: string; applies_to_system: string | null; certainty: string;
  discipline?: string | null; section?: string | null; edition?: string | null; url?: string | null; applies_in?: string | null; notes?: string | null;
}

export interface RfiRow { id: string; number: number | null; title: string | null; question: string | null; reason: string | null; source_ref: string | null; cost_schedule_impact: string | null; suggested_clarification: string | null; status: string | null }
export interface CoRow { id: string; title: string | null; description: string | null; why: string | null; source_ref: string | null; affected_system: string | null; risk_level: string | null; est_cost_impact_placeholder: string | null; est_schedule_impact_placeholder: string | null; recommendation: string | null }
export interface EstimateRow { id: string; version: number; final_sell_price: number; created_at: string; costing: Costing }

export interface RepriceSummary { repriced: number; still_internet: number; stale: number; by_source: Record<string, number> }
export interface TravelRow { id: string; type: string; qty: number | null; unit_cost: number | null; crew_size: number | null; days: number | null; distance_mi: number | null; notes: string | null }
export interface MarkupRow { name: string; pct_or_amount: number; basis: string | null }
export interface Costing {
  wage: { type: string; state: string | null; county: string | null; shift: string };
  material_cost: number; labor_hours: number; labor_cost: number;
  by_category: { category: string; classification: string; hours: number; rate: number; cost: number; rate_source: string | null; is_sample: boolean; overridden?: boolean }[];
  travel_total: number;
  markups: { name: string; pct: number; amount: number }[];
  material_markup: number; engineering: number; pm_supervision: number; commissioning: number; soft_costs: number;
  labor_factor: number; material_escalation: number;
  suggested_contingency_pct: number; risk: { total: number; needs_review: number; excluded: number };
  overhead: number; profit: number; sales_tax: number; freight: number;
  small_tools: number; contingency: number; bond: number; direct_cost: number; sell_price: number;
  total_cost: number; forecast_profit: number; margin_pct: number; crew_factor: number; warnings: string[];
  basis?: { gross_sqft: number | null; floors: number | null; idf_count: number | null; waste_pct: number | null; labor_factor: number | null; apprentice_ratio: number | null; apprentice_rate_pct: number | null; industry: string | null };
}
export interface UnitPrice {
  device_key: string; label: string; system: string;
  unit_material: number; unit_labor_hours: number; unit_labor_cost: number; unit_sell: number;
}
export interface PendingUser { id: string; email: string; name: string; role: string; created_at: string }
export interface UserRow { id: string; email: string; name: string; role: string; active: number; approved: number; created_at: string }
export interface LaborCalibration {
  category: string; factor: number; est_hours: number; actual_hours: number; jobs: number; note: string | null;
}
export interface WageRate {
  id: string; classification: string; state: string | null; county: string | null; wage_type: string;
  base_rate: number; fringe: number; burden_pct: number; effective_date: string | null;
  wage_source_ref: string | null; is_sample: number;
}

export interface BomResult { lines: number; total_material: number; total_labor_hours: number; no_assembly: string[]; uncounted: string[] }
export interface BomLine {
  id: string; system: string | null; symbol_id: string | null; part_number: string | null; manufacturer: string | null;
  qty: number | null; unit: string | null; description: string | null;
  unit_material_cost: number | null; extended_material_cost: number | null;
  labor_unit_hours: number | null; extended_labor_hours: number | null; labor_category: string | null;
  price_source: string | null; confidence_score: number | null; source_reference: string | null; notes: string | null;
}
export interface BomPayload { lines: BomLine[]; totals: { material: number; labor_hours: number; line_count: number } }

export interface CountSheetRowData {
  symbol_id: string; system_name: string | null; symbol_tag: string | null; legend_description: string | null;
  device_type: string | null; spec_reference: string | null; sheets_present: string | null;
  assembly_hint: string | null; confidence: number | null; map_status: string | null; count: number | null;
  symbol_image_ref?: string | null;
  symbol_image_url?: string | null;
}
export interface CountSheetPayload { project: { id: string; name: string }; rows: CountSheetRowData[] }
export interface ValidationReport {
  total_symbols: number; rows_received: number; accepted: number;
  blanks: string[]; negatives: string[]; non_numeric: string[]; unmatched: string[]; duplicates: string[]; ok: boolean;
}

export type MappingStatus = 'proposed' | 'confirmed' | 'needs_review' | 'excluded';
export interface MappingRow {
  symbol_id: string; device_key: string | null; device_type: string | null; assembly_hint: string | null;
  confidence: number; status: MappingStatus; source: string; ofci: number;
  symbol_tag: string | null; legend_description: string | null; system_name: string | null; sheets_present: string | null;
}
export interface MappingGate {
  total: number; confirmed: number; proposed: number; needs_review: number; excluded: number; blocks_bom: boolean;
}
export interface DeviceOption { key: string; label: string; system: string; assemblyHint: string }
export interface MappingPayload { mappings: MappingRow[]; gate: MappingGate; devices: DeviceOption[] }

export interface ExtractionRow { field: string; value: string; source_ref: string; confidence: number; status: string }
export interface SystemRow {
  id: string; division: number; system_code: string | null; system_name: string;
  selected: number; auto_selected: number; confidence: number | null; evidence: string | null;
}
export interface SymbolRow {
  id: string; system_id: string | null; symbol_tag: string | null;
  legend_description: string | null; spec_reference: string | null; sheets_present: string | null;
}
export interface CodeRow { level: string; code: string; title: string; applies_to_system: string | null; certainty: string; source_ref: string }
export interface AnalysisPayload {
  project: ProjectRow;
  extraction: ExtractionRow[];
  systems: SystemRow[];
  symbols: SymbolRow[];
  codes: CodeRow[];
  documents: { id: string; filename: string; r2_key: string | null; uploaded_at: string }[];
}

export interface ProjectRow {
  id: string;
  name: string;
  customer_name: string | null;
  gc_name: string | null;
  county: string | null;
  state: string | null;
  bid_date: string | null;
  bid_due_date?: string | null;
  architect?: string | null;
  engineer?: string | null;
  pw_jurisdiction?: string | null;
  davis_bacon?: number | null;
  estimator: string | null;
  salesperson: string | null;
  status: string;
  created_by?: string | null;
  creator_name?: string | null;
  is_fa?: number | null;
  bid_score?: string | null;
  outcome?: 'won' | 'lost' | 'no_bid' | null;
  outcome_amount?: number | null;
  competitor?: string | null;
  outcome_reason?: string | null;
  outcome_at?: string | null;
  created_at: string;
  updated_at: string;
}
